import { Button } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Card,
  CardContent,
  CardHeader,
  Skeleton,
} from '@kgc/ui';
import { useMemo, useState } from 'react';
import { WorkflowCard } from './components/WorkflowCard';
import { CATEGORY_CONFIG } from './constants';
import { ExecuteWorkflowDialog } from './dialogs/ExecuteWorkflowDialog';
import { ScheduleWorkflowDialog } from './dialogs/ScheduleWorkflowDialog';
import { useFavorites } from './hooks/useFavorites';
import type { FlowTab, PublishedWorkflow, WorkflowCategory } from './types';

const STORAGE_KEY = 'flow-accordion-state';

function getStoredOpenCategories(): string[] | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as string[];
  } catch {
    /* ignore corrupt data */
  }
  return null;
}

function saveOpenCategories(categories: string[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(categories));
  } catch {
    /* QuotaExceededError — state still updates in memory */
  }
}

interface CategoryGroup {
  category: WorkflowCategory;
  workflows: PublishedWorkflow[];
}

interface FlowCatalogTabProps {
  workflows: PublishedWorkflow[];
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  onTabChange?: ((tab: FlowTab) => void) | undefined;
}

export function FlowCatalogTab({
  workflows,
  isLoading,
  error,
  onRetry,
  onTabChange,
}: FlowCatalogTabProps) {
  const { t } = useTranslation();
  const { favorites, toggleFavorite, isFavorite } = useFavorites();
  const [selectedWorkflow, setSelectedWorkflow] = useState<PublishedWorkflow | null>(null);
  const [scheduleWorkflow, setScheduleWorkflow] = useState<PublishedWorkflow | null>(null);

  const favoriteWorkflows = useMemo(
    () => workflows.filter(wf => favorites.includes(wf.id)),
    [workflows, favorites]
  );

  const groups = useMemo<CategoryGroup[]>(() => {
    const byCategory = new Map<WorkflowCategory, PublishedWorkflow[]>();
    for (const wf of workflows) {
      const cat = wf.category ?? ('operations' as WorkflowCategory);
      const list = byCategory.get(cat) ?? [];
      list.push(wf);
      byCategory.set(cat, list);
    }
    return [...byCategory.entries()]
      .map(([category, wfs]) => ({ category, workflows: wfs }))
      .sort(
        (a, b) =>
          (CATEGORY_CONFIG[a.category]?.order ?? 99) - (CATEGORY_CONFIG[b.category]?.order ?? 99)
      );
  }, [workflows]);

  const allCategoryIds = useMemo(() => groups.map(g => g.category), [groups]);
  const storedValue = useMemo(getStoredOpenCategories, []);

  const [userOverride, setUserOverride] = useState<string[] | null>(null);
  const openCategories = userOverride ?? storedValue ?? allCategoryIds;

  function handleValueChange(value: string[]) {
    setUserOverride(value);
    saveOpenCategories(value);
  }

  if (isLoading && workflows.length === 0) {
    return (
      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
        data-testid="flow-catalog-skeleton"
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-5 w-3/4" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-4 w-1/2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div
          className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-center"
          data-testid="flow-catalog-error"
        >
          <p className="text-red-700 dark:text-red-300">{t('integration.flow.errorDescription')}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={onRetry}
            data-testid="flow-catalog-retry-btn"
          >
            {t('integration.retry')}
          </Button>
        </div>
      )}

      {!isLoading && workflows.length === 0 && !error && (
        <div
          className="flex flex-col items-center justify-center py-12 text-muted-foreground"
          data-testid="flow-catalog-empty"
        >
          <p className="text-sm">{t('integration.flow.noWorkflows')}</p>
        </div>
      )}

      {favoriteWorkflows.length > 0 && (
        <div data-testid="flow-favorites-section" className="mb-4">
          <h3 className="text-sm font-medium mb-2 flex items-center gap-1">
            <span aria-hidden="true">⭐</span> {t('integration.flow.favorites')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {favoriteWorkflows.map(wf => (
              <WorkflowCard
                key={wf.id}
                workflow={wf}
                isFavorite={true}
                onToggleFavorite={() => toggleFavorite(wf.id)}
                onExecute={() => setSelectedWorkflow(wf)}
                onSchedule={() => setScheduleWorkflow(wf)}
              />
            ))}
          </div>
        </div>
      )}

      {groups.length > 0 && (
        <Accordion type="multiple" value={openCategories} onValueChange={handleValueChange}>
          {groups.map(({ category, workflows: categoryWorkflows }) => {
            const config = CATEGORY_CONFIG[category];
            const CategoryIcon = config?.icon;
            return (
              <AccordionItem
                key={category}
                value={category}
                data-testid={`flow-category-${category}`}
              >
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2 text-sm">
                    {CategoryIcon && (
                      <CategoryIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    )}
                    <span>{t(config?.labelKey ?? category)}</span>
                    <span className="text-muted-foreground">({categoryWorkflows.length})</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {categoryWorkflows.map(wf => (
                      <WorkflowCard
                        key={wf.id}
                        workflow={wf}
                        isFavorite={isFavorite(wf.id)}
                        onToggleFavorite={() => toggleFavorite(wf.id)}
                        onExecute={() => setSelectedWorkflow(wf)}
                        onSchedule={() => setScheduleWorkflow(wf)}
                      />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      <ExecuteWorkflowDialog
        workflow={selectedWorkflow}
        open={selectedWorkflow !== null}
        onOpenChange={open => {
          if (!open) setSelectedWorkflow(null);
        }}
        onSuccess={() => {
          setSelectedWorkflow(null);
          onTabChange?.('running');
        }}
      />

      <ScheduleWorkflowDialog
        workflow={scheduleWorkflow}
        open={scheduleWorkflow !== null}
        onOpenChange={open => {
          if (!open) setScheduleWorkflow(null);
        }}
        onSuccess={() => setScheduleWorkflow(null)}
      />
    </div>
  );
}
